<template>
  <button
    :type="type"
    :class="buttonClasses"
    :disabled="disabled || loading"
    @click="handleClick"
    v-bind="$attrs"
  >
    <i v-if="icon && !iconPos || iconPos === 'left'" :class="iconClasses"></i>
    <span v-if="$slots.default || label" :class="labelClasses">
      <slot>{{ label }}</slot>
    </span>
    <i v-if="icon && iconPos === 'right'" :class="iconClasses"></i>
    
    <!-- Loading spinner -->
    <svg 
      v-if="loading" 
      class="animate-spin -ml-1 mr-2 h-4 w-4 text-current" 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        class="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        stroke-width="4"
      ></circle>
      <path 
        class="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  </button>
</template>

<script>
export default {
  name: 'UIButton',
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    iconPos: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right', 'top', 'bottom'].includes(value)
    },
    severity: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'success', 'info', 'warning', 'help', 'danger'].includes(value)
    },
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['small', 'normal', 'large'].includes(value)
    },
    variant: {
      type: String,
      default: 'filled',
      validator: (value) => ['filled', 'outlined', 'text'].includes(value)
    },
    type: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    raised: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    text: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    plain: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClasses() {
      const classes = [
        'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
      ];

      // Size classes
      const sizeClasses = {
        small: 'px-3 py-2 text-sm',
        normal: 'px-4 py-2 text-base',
        large: 'px-6 py-3 text-lg'
      };
      classes.push(sizeClasses[this.size]);

      // Variant override from boolean props
      let currentVariant = this.variant;
      if (this.text) currentVariant = 'text';
      if (this.outlined) currentVariant = 'outlined';

      // Severity and variant combinations
      const variantClasses = this.getVariantClasses(currentVariant, this.severity);
      classes.push(...variantClasses);

      // Shape classes
      if (this.rounded) {
        classes.push('rounded-full');
      } else {
        classes.push('rounded-md');
      }

      // Shadow classes
      if (this.raised && !this.text && !this.outlined) {
        classes.push('shadow-lg hover:shadow-xl');
      }

      // Disabled state
      if (this.disabled || this.loading) {
        classes.push('opacity-50 cursor-not-allowed');
      } else {
        classes.push('cursor-pointer');
      }

      // Loading state
      if (this.loading) {
        classes.push('pointer-events-none');
      }

      return classes.join(' ');
    },
    iconClasses() {
      const classes = [this.icon];
      
      if (this.label || this.$slots.default) {
        if (this.iconPos === 'right') {
          classes.push('ml-2');
        } else {
          classes.push('mr-2');
        }
      }
      
      return classes.join(' ');
    },
    labelClasses() {
      return this.loading ? 'opacity-0' : '';
    }
  },
  methods: {
    getVariantClasses(variant, severity) {
      const severityColors = {
        primary: {
          filled: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
          outlined: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500',
          text: 'text-blue-600 hover:bg-blue-50 focus:ring-blue-500'
        },
        secondary: {
          filled: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
          outlined: 'border-2 border-gray-600 text-gray-600 hover:bg-gray-50 focus:ring-gray-500',
          text: 'text-gray-600 hover:bg-gray-50 focus:ring-gray-500'
        },
        success: {
          filled: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
          outlined: 'border-2 border-green-600 text-green-600 hover:bg-green-50 focus:ring-green-500',
          text: 'text-green-600 hover:bg-green-50 focus:ring-green-500'
        },
        info: {
          filled: 'bg-cyan-600 text-white hover:bg-cyan-700 focus:ring-cyan-500',
          outlined: 'border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 focus:ring-cyan-500',
          text: 'text-cyan-600 hover:bg-cyan-50 focus:ring-cyan-500'
        },
        warning: {
          filled: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
          outlined: 'border-2 border-yellow-600 text-yellow-600 hover:bg-yellow-50 focus:ring-yellow-500',
          text: 'text-yellow-600 hover:bg-yellow-50 focus:ring-yellow-500'
        },
        help: {
          filled: 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500',
          outlined: 'border-2 border-purple-600 text-purple-600 hover:bg-purple-50 focus:ring-purple-500',
          text: 'text-purple-600 hover:bg-purple-50 focus:ring-purple-500'
        },
        danger: {
          filled: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500',
          outlined: 'border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white focus:ring-red-500',
          text: 'text-red-500 hover:bg-red-50 focus:ring-red-500'
        }
      };

      return severityColors[severity][variant].split(' ');
    },
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event);
      }
    }
  }
};
</script> 